/* Collection-only disclosure UI. Existing engines still own filtering and purchases. */
(() => {
 'use strict';
 if (window.__collinoFilterDisclosures) return;
 window.__collinoFilterDisclosures = true;
 const controllers = new Map();
 let serial = 0;
 const mount = root => {
  if (controllers.has(root)) return;
  const abort = new AbortController(), signal = abort.signal;
  const seen = new WeakSet(), states = new Map();
  let frame = 0, applying = false;
  const keyFor = group => group.dataset.filterId || group.querySelector('summary')?.textContent.trim();
  const selected = row => !!row.querySelector('input:checked, [aria-checked="true"], [aria-current="true"]') || row.hasAttribute('aria-current');
  const sync = () => {
   frame = 0;
   document.querySelectorAll('.globo-selected-items-wrapper .gf-block-title > .h3 > span').forEach(label => {
    if (label.textContent !== 'Applied filters') label.textContent = 'Applied filters';
   });
   document.querySelectorAll('.globo-selected-items-wrapper a[data-fid]').forEach(chip => {
    const label = `Remove filter ${chip.textContent.replace(/\s+/g, ' ').trim()}`;
    if (chip.getAttribute('aria-label') !== label) chip.setAttribute('aria-label', label);
   });
   const view = root.querySelector('.gf-filter-footer > button:last-child');
   view?.childNodes.forEach(node => {
    if (node.nodeType !== Node.TEXT_NODE) return;
    if (node.nodeValue.trim() === 'View') node.nodeValue = 'View ';
    else if (node.nodeValue.trim() === 'Products') node.nodeValue = ' products';
   });
   const close = root.querySelector('.gf-close-canvas');
   if (close && close.getAttribute('aria-label') !== 'Close filters') close.setAttribute('aria-label', 'Close filters');
   if (root.id === 'gf-tree' && root.getAttribute('role') === 'dialog' && root.getAttribute('aria-hidden') !== 'true') {
    const preview = document.getElementById('PBarNextFrameWrapper')?.getBoundingClientRect();
    const gap = preview && preview.top > 0 ? Math.max(0, innerHeight - preview.top) : 0;
    const height = `${Math.max(240, innerHeight - gap)}px`;
    if (root.style.getPropertyValue('--cf-drawer-height') !== height) root.style.setProperty('--cf-drawer-height', height);
   }
   root.querySelectorAll('.vc details, #gf-tree .gf-option-block').forEach(group => {
    const app = group.matches('.gf-option-block'), key = keyFor(group);
    if (!key) return;
    const rows = [...group.querySelectorAll(app ? '.gf-option-box > li' : ':scope > .vc-option, :scope > .tnp-option')];
    const searching = !!group.querySelector('input.gf-search')?.value.trim();
    // While searching, Globo owns each option's display state. Our six-row
    // disclosure must neither hide matching rows nor reveal inline-hidden ones.
    group.toggleAttribute('data-cf-searching', searching);
    group.querySelectorAll('input.gf-search').forEach(input => {
     input.dataset.cfOptionCount = String(rows.length);
     input.hidden = rows.length <= 6 && !input.value;
     const caption = group.querySelector('.gf-block-title [role="button"] > span')?.textContent.trim().toLocaleLowerCase('en-US') || 'options';
     const placeholder = `Search ${caption}…`;
     if (input.placeholder !== placeholder) input.placeholder = placeholder;
     if (input.getAttribute('aria-label') !== placeholder) input.setAttribute('aria-label', placeholder);
    });
    const header = app ? group.querySelector('.gf-block-title [role="button"]') : group.querySelector('summary');
    const container = app ? group.querySelector('.gf-block-content') : group;
    if (!header || !container || !rows.length) {
     group.querySelector('[data-cf-more]')?.remove();
     return;
    }
    if (!states.has(key)) states.set(key, { open: false, limit: 6 });
    const state = states.get(key);
    if (!seen.has(group)) {
     seen.add(group);
     if (!app) group.open = state.open;
    }
    if (app && (header.getAttribute('aria-expanded') === 'true') !== state.open) {
     applying = true; header.click(); applying = false;
    }
    let button = group.querySelector('[data-cf-more]');
    if (rows.length <= 6) {
     rows.forEach(row => { row.hidden = false; delete row.dataset.cfOption; });
     button?.remove();
     return;
    }
    if (!button) {
     button = document.createElement('button'); button.type = 'button'; button.dataset.cfMore = '';
     button.className = 'cf-more'; container.append(button);
    }
    if (!container.id) container.id = `CollectionFilterOptions-${++serial}`;
    button.setAttribute('aria-controls', container.id);
    button.hidden = searching;
    let remaining = 0;
    rows.forEach((row,index) => {
     const hide = !searching && index >= state.limit && !selected(row);
     row.dataset.cfOption = ''; row.hidden = hide; if (hide) remaining++;
    });
    const text = remaining ? `Show more (${remaining})` : 'Show less';
    if (button.textContent !== text) button.textContent = text;
    button.setAttribute('aria-expanded', String(state.limit > 6));
   });
   const clear = root.querySelector('.gf-filter-footer__clear');
   if (clear) clear.hidden = ![...root.querySelectorAll('[role=checkbox][aria-checked=true]')].some(button => !button.closest('[hidden]'));
  };
  const schedule = () => { if (!frame) frame = requestAnimationFrame(sync); };
  root.addEventListener('click', event => {
   if (event.target.closest('.gf-refine-toggle-mobile button')) schedule();
   const more = event.target.closest('[data-cf-more]');
   const group = event.target.closest('.vc details, #gf-tree .gf-option-block');
   if (!group || !states.has(keyFor(group))) return;
   const state = states.get(keyFor(group));
   if (more) {
    event.preventDefault();
    state.limit = more.textContent === 'Show less' ? 6 : state.limit + 6;
    schedule();
   } else if (!applying && event.isTrusted && event.target.closest('.gf-block-title [role="button"]')) {
    state.open = group.querySelector('[aria-expanded]')?.getAttribute('aria-expanded') !== 'true';
   }
  }, { capture: true, signal });
  root.addEventListener('keydown', event => {
   const header = event.target.closest('.gf-block-title [role=button]');
   if (header && ['Enter', ' '].includes(event.key)) {
    const group = header.closest('.gf-option-block'), state = states.get(keyFor(group));
    if (state) state.open = header.getAttribute('aria-expanded') !== 'true';
   }
  }, { capture: true, signal });
  root.addEventListener('toggle', event => {
   if (event.target.matches('.vc details') && states.has(keyFor(event.target))) states.get(keyFor(event.target)).open = event.target.open;
  }, { capture: true, signal });
  root.addEventListener('change', schedule, { signal });
  window.addEventListener('resize', schedule, { signal, passive: true });
  root.addEventListener('input', event => { if (event.target.matches('input.gf-search')) schedule(); }, { signal });
  const observer = new MutationObserver(records => {
   if (records.some(r => r.type === 'childList' || r.target === root || r.target.matches('.gf-block-title [role=button], [role=checkbox]'))) schedule();
  });
  observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-expanded', 'aria-checked', 'aria-hidden', 'role'] });
  controllers.set(root, () => { abort.abort(); observer.disconnect(); cancelAnimationFrame(frame); });
  sync();
 };
 // Globo portals its mobile tree directly into body. Bind to the tree itself,
 // so listeners and state survive that move; native engines stay section-scoped.
 const init = scope => {
  scope.querySelectorAll('volantes-collection, tapas-native-pilot').forEach(el => mount(el.closest('.shopify-section') || el));
  scope.querySelectorAll('#gf-tree').forEach(mount);
 };
 const start = () => {
  init(document);
  const discovery = new MutationObserver(records => {
   controllers.forEach((dispose, root) => { if (!root.isConnected) { dispose(); controllers.delete(root); } });
   records.forEach(record => record.addedNodes.forEach(node => {
   if (node.nodeType !== 1) return;
   if (node.id === 'gf-tree') mount(node);
   else node.querySelectorAll('#gf-tree').forEach(mount);
  }));
  });
  discovery.observe(document.body, { childList: true, subtree: true });
 };
 if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true }); else start();
 document.addEventListener('shopify:section:load', event => init(event.target));
 document.addEventListener('shopify:section:unload', event => controllers.forEach((dispose, root) => { if (event.target === root || event.target.contains(root)) { dispose(); controllers.delete(root); } }));
})();
